import type { ChatMessage, FileUpload } from '@innermate/shared';
import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import type { StreamSegment } from './ToolCallChip';

interface Props {
  messages: ChatMessage[];
  /** 현재 스트리밍 중이면 true — 마지막에 assistant placeholder bubble 을 렌더한다. */
  isStreaming?: boolean;
  /** 스트리밍 중 도착 순서대로 쌓은 세그먼트 (text/toolcall 혼합). */
  streamSegments?: StreamSegment[];
  /** 방금 보낸 사용자 메시지 — 서버 invalidate 전에 낙관적으로 표시. */
  pendingUser?: { content: string; fileIds: string[] } | null;
  /** artifact tool chip 의 "패널 열기" 버튼 핸들러. */
  onArtifactClick?: (artifactId: string, version: number) => void;
  /** 답변 본문의 [[slug]] 위키링크 클릭 시 호출 — 우측 미리보기 패널 트리거. */
  onWikilinkPeek?: (slug: string) => void;
  /** [[slug]] chip 의 표시 텍스트를 페이지 제목으로 치환하기 위한 lookup. */
  slugToTitle?: Map<string, string>;
  /** 첨부 칩 status 룩업. 폴링되는 파일 목록에서 파생돼 ready/failed 로 reactive 갱신. */
  filesById?: Map<string, FileUpload>;
  /** 관리자가 응답 시간 표시를 켰는지. 켜진 경우에만 메시지에 응답 시간을 노출한다. */
  showResponseTime?: boolean;
  /** 스트리밍 중 placeholder 옆에 보여줄 실시간 elapsed (ms). null 이면 표시 안 함. */
  liveElapsedMs?: number | null;
  /** 마지막 user 메시지 인라인 편집 후 재전송. 제공 시 마지막 user 말풍선에 편집 버튼 노출. */
  onEditResend?: (newContent: string) => void;
}

export function MessageList({
  messages,
  isStreaming,
  streamSegments,
  pendingUser,
  onArtifactClick,
  onWikilinkPeek,
  slugToTitle,
  filesById,
  showResponseTime,
  liveElapsedMs,
  onEditResend,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 의도적으로 새 메시지/스트리밍 변경 시 자동 스크롤.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isStreaming, streamSegments?.length, pendingUser]);

  // role='tool' 메시지는 assistant chip 에 통합 표시되므로 별도 bubble 미렌더.
  // ask_user_choice + skill picker (skill_select 또는 router-pattern ask_user — 둘 다 metadata.skillId
  // 기록) 인 user 메시지도 미렌더 — server-side chooser 응답이라 LLM 컨텍스트엔 들어가야 하지만 사용자
  // 화면엔 자기 선택의 흔적이 보이지 않게. 구 메시지 (skillId 메타 없음) backward compat 으로 interruptKind
  // 'skill_select' 도 OR 로 유지.
  const visible = messages.filter((m) => {
    if (m.role === 'tool') return false;
    const meta = m.metadata as
      | { kind?: string; interruptKind?: string; skillId?: string }
      | null
      | undefined;
    if (
      meta?.kind === 'ask_user_choice' &&
      (meta.interruptKind === 'skill_select' || typeof meta.skillId === 'string')
    ) {
      return false;
    }
    return true;
  });

  // 마지막에 보이는 user 메시지(실제 입력)에만 편집 버튼을 노출한다. 대화가 idle 일 때만 — 스트리밍
  // 중이거나 방금 보낸 메시지가 낙관 표시 중이면 편집을 막는다.
  const editableUserId =
    onEditResend && !isStreaming && !pendingUser
      ? [...visible].reverse().find((m) => m.role === 'user')?.id
      : undefined;

  return (
    <div className="space-y-5">
      {visible.map((m) => (
        <div
          key={m.id}
          className={
            m.archivedAt && (m.role === 'assistant' || m.role === 'user') ? 'opacity-50' : undefined
          }
        >
          <MessageBubble
            message={m}
            onArtifactClick={onArtifactClick}
            onWikilinkPeek={onWikilinkPeek}
            slugToTitle={slugToTitle}
            filesById={filesById}
            showResponseTime={showResponseTime}
            onEditResend={m.id === editableUserId ? onEditResend : undefined}
          />
        </div>
      ))}
      {pendingUser ? (
        <MessageBubble
          message={{
            role: 'user',
            content: pendingUser.content,
            attachedFileIds: pendingUser.fileIds.length > 0 ? pendingUser.fileIds : null,
          }}
          filesById={filesById}
        />
      ) : null}
      {isStreaming ? (
        <MessageBubble
          message={{ role: 'assistant', content: '' }}
          streaming
          streamSegments={streamSegments ?? []}
          onArtifactClick={onArtifactClick}
          onWikilinkPeek={onWikilinkPeek}
          slugToTitle={slugToTitle}
          showResponseTime={showResponseTime}
          liveElapsedMs={liveElapsedMs ?? null}
        />
      ) : null}
      <div ref={bottomRef} />
    </div>
  );
}
